import { format as formatDate } from 'date-fns'
import { ja } from 'date-fns/locale'
import type { Todo, Project } from '../lib/supabase'

export type ExportFormat = 'markdown' | 'csv' | 'json'

interface ExportOptions {
  project: Project | null
  todos: Todo[]
  exportFormat: ExportFormat
  includeCompleted?: boolean
}

// ステータスの日本語表示
const statusLabels: Record<string, string> = {
  'pending': '未着手',
  'in_progress': '進行中',
  'completed': '完了',
  'deleted': '削除済み',
}

// Markdownフォーマットでエクスポート
function exportToMarkdown(options: ExportOptions): string {
  const { project, todos } = options
  const now = formatDate(new Date(), 'yyyy年MM月dd日 HH:mm', { locale: ja })
  const projectName = project?.name || 'すべてのプロジェクト'

  let md = `# ${projectName}\n\n`
  md += `> エクスポート日時: ${now}\n\n`

  if (project?.description) {
    md += `${project.description}\n\n`
  }

  // ステータス別にグループ化
  const groupedByStatus: Record<string, Todo[]> = {
    'pending': [],
    'in_progress': [],
    'completed': [],
    'deleted': [],
  }

  todos.forEach(todo => {
    const status = todo.status || 'pending'
    if (groupedByStatus[status]) {
      groupedByStatus[status].push(todo)
    }
  })

  // 未完了タスク（削除済みは除外）
  const incompleteTodos = [
    ...groupedByStatus['in_progress'],
    ...groupedByStatus['pending'],
  ]

  if (incompleteTodos.length > 0) {
    md += `## 未完了タスク (${incompleteTodos.length}件)\n\n`
    incompleteTodos.forEach(todo => {
      const checkbox = '- [ ]'
      const dueDate = todo.due_date ? ` (期限: ${formatDate(new Date(todo.due_date), 'yyyy/MM/dd')})` : ''
      const status = statusLabels[todo.status || 'pending']
      const assignee = todo.assigned_user?.nickname ? ` @${todo.assigned_user.nickname}` : ''
      md += `${checkbox} **${todo.title}** [${status}]${dueDate}${assignee}\n`
      if (todo.description) {
        md += `  - ${todo.description}\n`
      }
    })
    md += '\n'
  }

  // 完了タスク
  const completedTodos = groupedByStatus['completed']
  if (completedTodos.length > 0) {
    md += `## 完了タスク (${completedTodos.length}件)\n\n`
    completedTodos.forEach(todo => {
      const checkbox = '- [x]'
      const dueDate = todo.due_date ? ` (期限: ${formatDate(new Date(todo.due_date), 'yyyy/MM/dd')})` : ''
      const assignee = todo.assigned_user?.nickname ? ` @${todo.assigned_user.nickname}` : ''
      md += `${checkbox} ~~${todo.title}~~${dueDate}${assignee}\n`
    })
    md += '\n'
  }

  md += `---\n`
  md += `合計: ${todos.length}件 (完了: ${completedTodos.length}件, 未完了: ${incompleteTodos.length}件)\n`

  return md
}

// CSVフォーマットでエクスポート
function exportToCsv(options: ExportOptions): string {
  const { todos } = options

  // ヘッダー
  const headers = [
    'タイトル',
    'ステータス',
    '期限',
    '担当者',
    '説明',
    '作成日',
    '更新日',
  ]

  const rows = todos.map(todo => {
    return [
      `"${(todo.title || '').replace(/"/g, '""')}"`,
      `"${statusLabels[todo.status || 'pending']}"`,
      todo.due_date ? formatDate(new Date(todo.due_date), 'yyyy/MM/dd') : '',
      `"${(todo.assigned_user?.nickname || '').replace(/"/g, '""')}"`,
      `"${(todo.description || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`,
      todo.created_at ? formatDate(new Date(todo.created_at), 'yyyy/MM/dd HH:mm') : '',
      todo.updated_at ? formatDate(new Date(todo.updated_at), 'yyyy/MM/dd HH:mm') : '',
    ].join(',')
  })

  return [headers.join(','), ...rows].join('\n')
}

// JSONフォーマットでエクスポート
function exportToJson(options: ExportOptions): string {
  const { project, todos } = options

  const exportData = {
    project: project ? {
      name: project.name,
      description: project.description,
      color: project.color,
    } : {
      name: 'すべてのプロジェクト',
      description: null,
      color: null,
    },
    exportedAt: new Date().toISOString(),
    totalTasks: todos.length,
    tasks: todos.map(todo => ({
      title: todo.title,
      description: todo.description,
      status: todo.status,
      statusLabel: statusLabels[todo.status || 'pending'],
      dueDate: todo.due_date,
      assignedUser: todo.assigned_user?.nickname || null,
      createdAt: todo.created_at,
      updatedAt: todo.updated_at,
    })),
  }

  return JSON.stringify(exportData, null, 2)
}

// ファイルをダウンロード
function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// メインのエクスポート関数
export function exportTasks(options: ExportOptions): void {
  const { project, exportFormat } = options
  const timestamp = exportFormat === 'json' ? '' : `_${formatDate(new Date(), 'yyyyMMdd_HHmm')}`
  const projectName = project?.name || 'すべてのプロジェクト'
  const safeProjectName = projectName.replace(/[/\\?%*:|"<>]/g, '-')

  let content: string
  let filename: string
  let mimeType: string

  switch (exportFormat) {
    case 'markdown':
      content = exportToMarkdown(options)
      filename = `${safeProjectName}${timestamp}.md`
      mimeType = 'text/markdown;charset=utf-8'
      break
    case 'csv':
      content = '\uFEFF' + exportToCsv(options) // BOM for Excel
      filename = `${safeProjectName}${timestamp}.csv`
      mimeType = 'text/csv;charset=utf-8'
      break
    case 'json':
      content = exportToJson(options)
      filename = `${safeProjectName}.json`
      mimeType = 'application/json;charset=utf-8'
      break
    default:
      throw new Error(`Unknown export format: ${exportFormat}`)
  }

  downloadFile(content, filename, mimeType)
}

// フォーマットの表示名
export const formatLabels: Record<ExportFormat, string> = {
  markdown: 'Markdown (.md)',
  csv: 'CSV (.csv)',
  json: 'JSON (.json)',
}
